<script setup lang="ts">
import type { UnifiedChatContentRecommendation } from '~kui/types/contentRecommendation';
import type { RouteLocationRaw } from 'vue-router';

import { Badge } from '~ds/components/ui/badge';
import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';
import { useIpxBackgroundStyle } from '~kui/composables/useIpxBackgroundStyle';
import { useI18n } from 'vue-i18n';

import type { AssetCardFacts } from './assetCardAccessibleName';

import { useAssetCardName } from './assetCardAccessibleName';
import { coinPillVariant, coinRushApplies } from './assetCardSponsorship';

type CollectionRec = Extract<UnifiedChatContentRecommendation, { type: 'learning_asset_collection' }>;

interface Props {
  rec: CollectionRec;
  allowPartnerVariant?: boolean;
  fluid?: boolean;
  wide?: boolean;
  square?: boolean;
  /** When set, the card renders as a crawlable `<a>` (NuxtLink) to this destination. */
  to?: string | RouteLocationRaw;
  /** AU-only Coin Rush promo pill; gated on the coin-rush-enabled flag by the container. */
  coinRush?: boolean;
  /** Refuses activation on the inner control itself, so the card also leaves the tab order. */
  disabled?: boolean;
  /** Lays the Anyway Expo lockup over the artwork, for a lesson an expo is running. */
  showExpoLockup?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  allowPartnerVariant: true,
  fluid: false,
  wide: false,
  square: false,
  to: undefined,
  coinRush: false,
  disabled: false,
  showExpoLockup: false,
});

const emit = defineEmits<{
  click: [];
}>();

// Partner logo routed through ipx at its small display size (webp).
const partnerLogoStyle = useIpxBackgroundStyle(() => props.rec.partnerLogoUrl, { width: 80 });

const { t } = useI18n()

const cardName = useAssetCardName();

const isCompleted = computed(() => props.rec.completedCount === props.rec.totalCount && props.rec.totalCount > 0);
const pill = computed(() => coinPillVariant(coinRushApplies(props.coinRush, props.rec)));

/** What the card face shows, read by both the template and the card's name so the two cannot drift. */
const facts = computed<AssetCardFacts>(() => {
  const { partnerLogoUrl, partnerName, coins, duration, completedCount, totalCount, isSaved } = props.rec;
  const sponsor = partnerLogoUrl ? partnerName : null;

  if (isCompleted.value) {
    return { completed: true, sponsor, saved: isSaved };
  }

  return {
    sponsor,
    coinRush: coinRushApplies(props.coinRush, props.rec),
    coins,
    progress: completedCount > 0 && totalCount > 0 ? { completed: completedCount, total: totalCount } : null,
    duration,
    saved: isSaved,
  };
});

const accessibleName = computed(() => cardName(t('unified.common.assetCard.view', { title: props.rec.name }), facts.value));
</script>

<template>
  <KUIUnifiedAssetCollectionCardPartner
    v-if="rec.partnerLogoUrl && allowPartnerVariant"
    :rec="rec"
    :fluid="fluid"
    :to="to"
    :coin-rush="coinRush"
    :disabled="disabled"
    @click="emit('click')"
  />
  <KUIUnifiedAssetCardBase
    v-else
    :image-path="rec.imageUrl"
    :title="rec.name"
    :variant="fluid ? 'full' : 'default'"
    :wide="wide"
    :square="square"
    :to="to"
    :disabled="disabled"
    :aria-label="accessibleName"
    @click="emit('click')"
  >
    <template #header>
      <span class="flex justify-between">
        <span>
          <NuxtImg
            v-if="showExpoLockup"
            src="/images/illustrations/expo-lockup-right.webp"
            alt=""
            role="presentation"
            width="80"
            height="42"
            class="absolute top-2 left-2"
            data-testid="asset-collection-card-expo-lockup"
          />
          <span
            v-else-if="rec.partnerLogoUrl"
            class="absolute top-2 left-2 bg-white rounded-full size-10 bg-contain bg-center bg-no-repeat"
            :style="partnerLogoStyle"
          />
        </span>
        <span v-if="isCompleted" class="bg-secondary size-8 rounded-full flex justify-center items-center">
          <Icon :name="ICONS.tickSolid" size="lg" />
        </span>
        <span v-else-if="facts.coins" class="bg-secondary rounded-full p-1 flex items-center self-start gap-0.5">
          <span class="text-xs font-medium ps-1">{{ t(pill.key, { coins: facts.coins }) }}</span>
          <NuxtImg
            alt=""
            role="presentation"
            class="w-4 h-auto"
            :src="pill.coinSrc"
          />
        </span>
      </span>
    </template>
    <template v-if="!isCompleted || facts.saved" #extra>
      <span class="flex flex-wrap gap-1 mt-1">
        <Badge
          v-if="facts.progress"
          as="span"
          class="py-1.5!"
          variant="outline"
        >
          {{ t('unified.common.progressCount', facts.progress) }}
        </Badge>
        <Badge
          v-if="facts.duration"
          as="span"
          variant="outline"
          :uppercase="false"
        >
          <Icon
            :name="ICONS.clock"
            size="sm"
            class="shrink-0"
          />
          {{ rec.completedCount > 0 ? `${rec.duration}${t('unified.common.minShort')}` : `${rec.duration} ${t('unified.common.min')}` }}
        </Badge>
        <KUIUnifiedSavedTag v-if="facts.saved" />
      </span>
      <KUITopicProgressBar
        v-if="facts.progress"
        :progress-percent="Math.round((facts.progress.completed / facts.progress.total) * 100)"
        :sections="5"
        :height="5"
        class="mt-1"
      />
    </template>
  </KUIUnifiedAssetCardBase>
</template>
